import React from "react";
import {Box, NumberInput, Text} from "@mantine/core";
import classes from "./PPReliefEventDetail.module.css";
import cx from "clsx";

export const RELIEF_LEVELS = [
    {score: 0, label: "No Relief", color: "rgb(220 38 38)"},
    {score: 10, label: "Low Relief", color: "rgb(239 68 68)"},
    {score: 20, label: "Low Relief", color: "rgb(234 88 12)"},
    {score: 30, label: "Low Relief", color: "rgb(249 115 22)"},
    {score: 40, label: "Moderate Relief", color: "rgb(251 146 60)"},
    {score: 50, label: "Moderate Relief", color: "rgb(234 179 8)"},
    {score: 60, label: "Moderate Relief", color: "rgb(250 204 21)"},
    {score: 70, label: "High Relief", color: "rgb(163 230 53)"},
    {score: 80, label: "High Relief", color: "rgb(132 204 22)"},
    {score: 90, label: "High Relief", color: "rgb(34 197 94)"},
    {score: 100, label: "Complete Relief", color: "rgb(22 163 74)"},
] as const;

export interface PPReliefEventDetailProps {
    score?: number;
    onChange?: (score: number | undefined) => void;
    readonly?: boolean;
    showLabel?: boolean;
    onPauseRequest?: () => void;
}

const roundToTen = (n: number) => Math.round(n / 10) * 10;

export const PPReliefEventDetail: React.FC<PPReliefEventDetailProps> = ({
    score,
    onChange,
    readonly = false,
    showLabel = false,
    onPauseRequest,
}) => {
    const roundedScore = score !== undefined ? roundToTen(score) : undefined;

    const handleBoxClick = (levelScore: number) => {
        if (onPauseRequest) {
            onPauseRequest();
        }
        if (!readonly && onChange) {
            onChange(levelScore);
        }
    };

    const handleNumberChange = (val: string | number) => {
        if (readonly || !onChange) return;
        const n = typeof val === "string" ? parseInt(val, 10) : val;
        if (!isNaN(n) && n >= 0 && n <= 100) {
            onChange(n);
        } else if (val === "" || (typeof val === "string" && val.trim() === "")) {
            onChange(undefined);
        }
    };

    return (
        <Box className={classes.container}>
            <NumberInput
                className={classes.numberInput}
                size="xs"
                label={showLabel ? "% Pain Relief" : undefined}
                value={score ?? ""}
                disabled={readonly}
                min={0}
                max={100}
                step={10}
                clampBehavior="strict"
                placeholder="0 – 100"
                onChange={handleNumberChange}
                onFocus={() => {
                    if (onPauseRequest) {
                        onPauseRequest();
                    }
                }}
            />

            <Box className={classes.scoreRow}>
                {RELIEF_LEVELS.map((level) => {
                    const isSelected = roundedScore !== undefined && roundedScore === level.score;
                    return (
                        <Box key={level.score} className={classes.scoreCell}>
                            <Box
                                className={cx(classes.scoreItem, isSelected && classes.selected)}
                                style={{
                                    borderColor: isSelected ? level.color : undefined,
                                    cursor: readonly ? "default" : "pointer",
                                }}
                                title={`${level.score}% – ${level.label}`}
                                onClick={() => handleBoxClick(level.score)}
                            >
                                <Text
                                    size="xs"
                                    fw={isSelected ? 700 : 600}
                                    className={classes.scoreText}
                                >
                                    {level.score}%
                                </Text>
                            </Box>
                            <Box
                                className={classes.colorBar}
                                style={{backgroundColor: level.color}}
                            />
                        </Box>
                    );
                })}
            </Box>
        </Box>
    );
};
